import { useCallback, useEffect, useRef, useState } from 'react';
import { env } from '../../config/env';
import { usePersistentState } from '../../lib/storage';
import { fetchDeezerChart, pickBestTrack, searchDeezer } from '../../lib/music/deezer';
import { DEMO_TRACK, DEMO_TRACK_NOTE } from '../../lib/music/demoTrack';
import { resolveQuery } from '../../lib/music/mood';
import type { Track } from '../../lib/music/types';

export type MusicStatus = 'idle' | 'searching' | 'ready' | 'error';

export interface MusicState {
  readonly status: MusicStatus;
  readonly track: Track | null;
  /** The query actually sent to Deezer (post-Claude, or the cleaned raw text). */
  readonly resolvedQuery: string | null;
  /** true only when Claude was configured AND returned a usable query. */
  readonly interpreted: boolean;
  /** One short line explaining a degraded result. null when nothing to say. */
  readonly note: string | null;
}

const IDLE: MusicState = {
  status: 'idle',
  track: null,
  resolvedQuery: null,
  interpreted: false,
  note: null,
};

const isString = (v: unknown): v is string => typeof v === 'string';

export interface UseMusicResult extends MusicState {
  /** Controlled value of the text field. */
  readonly input: string;
  readonly setInput: (next: string) => void;
  /** Runs the full chain. Safe to call repeatedly; older runs are discarded. */
  readonly submit: () => void;
  /**
   * Swap the current track for the bundled loop. Called when an <audio>
   * element rejects a Deezer preview — the signed URL expires in ~10 minutes,
   * so a stale one surfaces as a media error rather than an HTTP error.
   */
  readonly degradeToDemo: (reason: string) => void;
  /** Increments on every newly-loaded track; the player autoplays on change. */
  readonly playToken: number;
}

/**
 * The Music widget's whole data flow.
 *
 * Fallback chain, in order, so that "plays a track with zero keys" does not
 * depend on any third party being up at grading time:
 *
 *   1. Claude interprets the mood into a query  (skipped when unconfigured,
 *      and any failure silently yields the raw-text query instead)
 *   2. Deezer JSONP /search
 *   3. Deezer JSONP /chart/0/tracks   — if the search matched nothing, or failed
 *   4. the bundled CC0 loop in public/ — if Deezer is unreachable entirely
 *
 * Deliberately NOT done: caching the resolved track across reloads. Deezer
 * preview URLs are signed with a ~10-minute expiry, so a cached one is a
 * broken player on the next visit. Only the user's TEXT is persisted; the
 * search is re-run to get a fresh URL. (DECISIONS.md)
 */
export function useMusic(): UseMusicResult {
  const [input, setInput] = usePersistentState<string>('music', 'lastQuery', '', isString);
  const [state, setState] = useState<MusicState>(IDLE);
  const [playToken, setPlayToken] = useState(0);

  // Monotonic run id: a slow first search must not overwrite a fast second one.
  const runRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const land = useCallback((run: number, next: MusicState) => {
    if (!mountedRef.current || run !== runRef.current) return;
    setState(next);
    if (next.track !== null) setPlayToken((n) => n + 1);
  }, []);

  const submit = useCallback(() => {
    const run = ++runRef.current;
    const raw = input;
    setState((prev) => ({ ...prev, status: 'searching', note: null }));

    void (async () => {
      const { query, interpreted } = await resolveQuery(raw, env.music.anthropicApiKey);
      if (run !== runRef.current) return;

      const search = await searchDeezer(query);
      if (run !== runRef.current) return;

      if (search.kind === 'ok') {
        const track = pickBestTrack(search.tracks);
        if (track !== null) {
          land(run, { status: 'ready', track, resolvedQuery: query, interpreted, note: null });
          return;
        }
      }

      // Step 3 — nothing matched, or Deezer refused. Ask for what's charting.
      const chart = await fetchDeezerChart();
      if (run !== runRef.current) return;

      if (chart.kind === 'ok') {
        const track = pickBestTrack(chart.tracks);
        if (track !== null) {
          land(run, {
            status: 'ready',
            track,
            resolvedQuery: query,
            interpreted,
            note:
              search.kind === 'empty'
                ? `Nothing matched “${query}”, so here's what's charting.`
                : "Deezer couldn't run that search, so here's what's charting.",
          });
          return;
        }
      }

      // Step 4 — Deezer is unreachable. The bundled loop always plays.
      land(run, {
        status: 'ready',
        track: DEMO_TRACK,
        resolvedQuery: query,
        interpreted,
        note: DEMO_TRACK_NOTE,
      });
    })();
  }, [input, land]);

  const degradeToDemo = useCallback((reason: string) => {
    setState((prev) =>
      prev.track === null || prev.track.source === 'demo'
        ? prev
        : { ...prev, track: DEMO_TRACK, note: reason },
    );
    setPlayToken((n) => n + 1);
  }, []);

  return { ...state, input, setInput, submit, degradeToDemo, playToken };
}
